import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react"

import {
    getCurrentUser,
    signIn as supabaseSignIn,
    signOut as supabaseSignOut,
    supabase,
} from "@/lib/supabase"
import { LOGIN_EMAIL_CACHE_KEY, USER_CACHE_KEY } from "@/constants/storage"

const AuthContext = createContext(undefined)

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null)
    const [loading, setLoading] = useState(false)
    const [initializing, setInitializing] = useState(true)
    const [error, setError] = useState(null)
    // Bandera: el SIGNED_OUT que viene tras logout() explícito SÍ debe limpiar el state.
    // El SIGNED_OUT por expiración/fallo de refresh NO debe cerrar la sesión local.
    const explicitLogoutRef = useRef(false)

    useEffect(() => {
        let mounted = true
        // Safety: si la inicialización tarda más de 4s, la destrabamos
        const initTimer = setTimeout(() => {
            if (mounted) setInitializing(false)
        }, 4000)

        const loadUser = async () => {
            setInitializing(true)
            try {
                const { user: storedUser } = await getCurrentUser()
                if (!mounted) return
                setUser(storedUser)
            } catch (err) {
                console.warn("[auth] loadUser failed:", err?.message)
            } finally {
                if (mounted) {
                    clearTimeout(initTimer)
                    setInitializing(false)
                }
            }
        }

        loadUser()

        // Sync con cambios de Supabase Auth (login/logout en otra pestaña, refresh token, etc)
        const { data: authListener } = supabase.auth.onAuthStateChange(async (event, session) => {
            if (event === "SIGNED_OUT") {
                // Si fue logout EXPLÍCITO desde la app, sí limpiamos
                if (explicitLogoutRef.current) {
                    explicitLogoutRef.current = false
                    setUser(null)
                    return
                }
                // SIGNED_OUT espontáneo (token expirado, refresh fallido, otra pestaña):
                // si tenemos usuario en localStorage (legacy o cache), conservamos sesión local.
                try {
                    const raw = typeof window !== "undefined"
                        ? localStorage.getItem(USER_CACHE_KEY)
                        : null
                    if (raw) {
                        const cached = JSON.parse(raw)
                        if (cached?.email) {
                            // Mantenemos la sesión basada en el cache local
                            setUser(cached)
                            return
                        }
                    }
                } catch { /* ignore */ }
                setUser(null)
                return
            }
            if (event === "SIGNED_IN" || event === "TOKEN_REFRESHED") {
                if (session?.user?.email) {
                    const { user: hydrated } = await getCurrentUser()
                    if (hydrated) setUser(hydrated)
                }
            }
        })

        return () => {
            mounted = false
            clearTimeout(initTimer)
            authListener?.subscription?.unsubscribe()
        }
    }, [])

    const login = async (email, password = null) => {
        setLoading(true)
        setError(null)

        const result = await supabaseSignIn(email, password)

        if (result.error) {
            setError(result.error)
            setLoading(false)
            return result
        }

        if (result.data?.user) {
            setUser(result.data.user)
        }

        setLoading(false)
        return result
    }

    const logout = async () => {
        // Marcar logout EXPLÍCITO antes de signOut para que el listener limpie
        explicitLogoutRef.current = true
        await supabaseSignOut()
        try {
            if (typeof window !== "undefined") {
                localStorage.removeItem(LOGIN_EMAIL_CACHE_KEY)
            }
        } catch (err) {
            console.warn("[auth] no se pudo limpiar el login cache", err)
        }
        setUser(null)
    }

    const refreshUser = async () => {
        const { user: currentUser } = await getCurrentUser()
        setUser(currentUser)
        return currentUser
    }

    const value = useMemo(
        () => ({
            user,
            loading,
            initializing,
            error,
            login,
            logout,
            refreshUser,
            clearError: () => setError(null),
        }),
        [user, loading, initializing, error],
    )

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
    const context = useContext(AuthContext)

    if (context === undefined) {
        throw new Error("useAuth must be used within an AuthProvider")
    }

    return context
}




